import { Fragment } from 'react'
import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface Step {
  id: string | number
  label: string
  status: 'completed' | 'current' | 'pending'
}

interface StepNavigationProps {
  steps: Step[]
  className?: string
}

export function StepNavigation({ steps, className }: StepNavigationProps) {
  return (
    <nav
      className={cn(
        'flex h-24 w-full flex-row items-center justify-between border-b border-[#D4D4D4] bg-[#F5F5F5] px-4',
        className
      )}
    >
      {steps.map((step, index) => (
        <Fragment key={step.id}>
          {/* Step container */}
          <div className="flex flex-col items-center gap-2">
            {/* Circle button */}
            <div
              className={cn(
                'flex h-9 min-h-9 w-9 min-w-9 items-center justify-center rounded-full',
                {
                  // Completed or Current state
                  'bg-[#1E293B]':
                    step.status === 'completed' || step.status === 'current',
                  // Pending state
                  'border border-[#D4D4D4] bg-[#F5F5F5] opacity-50 shadow-[0px_1px_2px_rgba(0,0,0,0.05)]':
                    step.status === 'pending',
                }
              )}
            >
              {step.status === 'completed' ? (
                <Check className="h-4 w-4 text-[#FAFAFA]" strokeWidth={2} />
              ) : (
                <span
                  className={cn(
                    'text-center text-sm font-medium leading-5 tracking-[0.005em]',
                    {
                      'text-[#FAFAFA]': step.status === 'current',
                      'text-[#737373]': step.status === 'pending',
                    }
                  )}
                >
                  {index + 1}
                </span>
              )}
            </div>

            {/* Label */}
            <span
              className={cn(
                'whitespace-nowrap text-center text-sm font-medium leading-5 tracking-[0.005em]',
                {
                  'text-[#262626]':
                    step.status === 'completed' || step.status === 'current',
                  'text-[#737373]': step.status === 'pending',
                }
              )}
            >
              {step.label}
            </span>
          </div>

          {/* Separator */}
          {index < steps.length - 1 && (
            <div className="mx-4 flex h-px flex-1 items-center rounded-sm">
              <div
                className={cn('h-px w-full', {
                  'bg-[#1E293B]': step.status === 'completed',
                  'bg-[#D4D4D4]':
                    step.status === 'current' || step.status === 'pending',
                })}
              />
            </div>
          )}
        </Fragment>
      ))}
    </nav>
  )
}
